import { AutogenLayoutDriverPayRateSlideout } from "./autogen/AutogenLayoutDriverPayRateSlideout";
import {
    AddType, DataSource, DataSourceMode, DataSourceValidationEvent, EditRowDecorator, Layout, Table, TableRow,
    ValidationResult
} from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { DriverPayRateDetails } from "./DriverPayRateDetails";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

export class DriverPayRateSlideout extends AutogenLayoutDriverPayRateSlideout {
    private allRateData: DataSource;

    async onLoad(): Promise<void> {
        // Add validation listener
        this.sourceDrsPayrateHeader.addValidationListener((event: DataSourceValidationEvent) => {
            this.validatePayRate(event);
        });
    }

    private validatePayRate(event?: DataSourceValidationEvent): ValidationResult[] {
        const activeRow = this.sourceDrsPayrateHeader.activeRow;
        const errors: string[] = [];

        this.validateDateRange(activeRow, errors);

        // Validation: Check for existing record with same customer and overlapping effective date
        this.validateDateOverlap(activeRow, errors);

        // Validation: Check for duplicate rate detail entries matching the same criteria as legacy client
        this.validateDuplicateRateDetails(activeRow, errors);

        // Create ValidationResult array to return
        const validationResults: ValidationResult[] = [];
        errors.forEach(errorMessage => {
            validationResults.push({
                isValid: false,
                component: this,
                caption: errorMessage,
                validationMessage: errorMessage
            });
        });
        return validationResults;
    }

    private validateDuplicateRateDetails(activeRow: ModelRow, errors: string[]): void {
        const details: ModelRow[] = (this.layoutPayRateDetail?.sourceDrsPayrate?.data as ModelRow[]) || [];
        if (!details || details.length === 0) {
            return;
        }
        const fieldsToMatch = [
            "experience_months",
            "driver_rating",
            "beginning_distance",
            "ending_distance",
            "order_type_id",
            "rate_method"
        ];

        const groupCode = activeRow?.get("id") || "";

        for (let i = 0; i < details.length; i++) {
            const a = details[i];
            for (let j = 0; j < details.length; j++) {
                if (i === j) continue;
                // Skip if working on the same row (compare primary keys when available)
                const aPk = a.get("primary_key");
                const bPk = details[j].get("primary_key");
                if (aPk && bPk && aPk === bPk) continue;

                let matches = true;
                for (const f of fieldsToMatch) {
                    const av = a.get(f);
                    const bv = details[j].get(f);
                    // Normalize undefined/null to empty string for comparison
                    const avNorm = av == null ? "" : String(av);
                    const bvNorm = bv == null ? "" : String(bv);
                    if (avNorm !== bvNorm) {
                        matches = false;
                        break;
                    }
                }

                if (matches) {
                    const label = groupCode ? ` for pay rate group [${groupCode}]` : "";
                    errors.push(`Pay rate already exists${label}.`);
                    return; // Stop at first duplicate found
                }
            }
        }
    }

    private validateDateOverlap(activeRow: ModelRow, errors: string[]): void {
        const customerId = activeRow.get("customer_id");
        const effectiveDate = activeRow.getDate("effective_dt");
        const expirationDate = activeRow.getDate("expiration_dt");

        if (!this.allRateData || !this.allRateData.data) {
            return;
        }

        // Check for overlapping date ranges with existing records
        const existingRecords = this.allRateData.data as ModelRow[];

        // Check for date overlaps with existing records
        for (const existingRecord of existingRecords) {
            // Only check records with the same customer, skip the current record being edited
            if (existingRecord.get("customer_id") !== customerId || existingRecord.get("primary_key") === activeRow.get("primary_key")) {
                continue;
            }

            // Convert dates for overlap checking
            const existingEffective = existingRecord.getDate("effective_dt");
            const existingExpiration = existingRecord.getDate("expiration_dt");

            // Check if dates overlap
            if (this.datesOverlap(effectiveDate, expirationDate, existingEffective, existingExpiration)) {
                const payRateGroupCode = existingRecord.get("id") || "no pay rate group";

                const customerName = this.textboxCustomerId.getFirstLookupModelData()?.get("name") || "no customer";
                errors.push(`Pay Rate Group [${payRateGroupCode}] for Customer [${customerName}] already exists for this date range.`);
                break; // Only show first overlap found
            }
        }
    }

    private datesOverlap(start1: Date, end1: Date | null, start2: Date, end2: Date | null): boolean {
        // if value is null, treat it as an infinite date range
        start1 = start1 || new Date(0); // Default to epoch if null
        end1 = end1 || new Date(8640000000000000); // Default to max date if null
        start2 = start2 || new Date(0); // Default to epoch if null
        end2 = end2 || new Date(8640000000000000); // Default to max date if null

        // Two date ranges overlap if:
        // start1 <= actualEnd2 AND start2 <= actualEnd1
        return start1 <= end2 && start2 <= end1;
    }

    private validateDateRange(activeRow: ModelRow, errors: string[]): void {
        const effectiveDate = activeRow.get("effective_dt");
        const expireDate = activeRow.get("expiration_dt");

        if (effectiveDate && expireDate) {
            const effective = new Date(effectiveDate);
            const expire = new Date(expireDate);

            if (expire < effective) {
                errors.push("Expiration Date cannot be before the Effective Date.");
            }
        }
    }

    public static async openSlideout(
        mode: "add" | "update" = "add",
        options: {
            selectedRow?: TableRow;
            parentDataSource: DataSource;
            sourceDrsPayrateHeader: DataSource;
            tableDriverPayRates: Table;
            rateDetailsMap: Map<string, DriverPayRateDetails>;
        }
    ): Promise<EditRowDecorator> {
        const {
            selectedRow,
            parentDataSource,
            sourceDrsPayrateHeader,
            tableDriverPayRates,
            rateDetailsMap,
        } = options;

        const layout = Layout.getLayout("lme/settlement/DriverPayRateSlideout") as DriverPayRateSlideout;
        await layout.ensureLoaded();
        layout.layoutPayRateDetail.tableDriverPayRateDetails.addType = AddType.QUICK;
        layout.layoutPayRateDetail.tableDriverPayRateDetails.allowEdit = true;
        layout.layoutPayRateDetail.tableDriverPayRateDetails.allowDelete = true;
        layout.layoutPayRateDetail.tableDriverPayRateDetails.displayToolsCell = true;
        layout.allRateData = sourceDrsPayrateHeader;
        let data: ModelRow;
        layout.layoutPayRateDetail.tableDriverPayRateDetails.dataSource.parentDataSource = new DataSource(); // Fake parent to avoid "are you sure" dialog

        if (mode === "add") {
            data = await sourceDrsPayrateHeader.createBlankRow();
            layout.layoutPayRateDetail.initialize(data.createBasicCopy(), []);
        } else {
            if (!selectedRow) {
                throw new Error("Selected row is required for update mode");
            }
            const sourceDrsPayrate = rateDetailsMap.get(selectedRow.data.get("primary_key")).sourceDrsPayrate;
            const copyData = sourceDrsPayrate.getDataCopy();
            const selectedRowData = selectedRow.data.createBasicCopy();
            layout.layoutPayRateDetail.initialize(selectedRowData, copyData || []);
            data = selectedRowData;
            selectedRow.setExpandedAsync(false);
        }

        return new EditRowDecorator({
            layout: layout,
            title: `${mode === "add" ? "Add" : "Update"} Driver Pay Rate`,
            dataSourceMode: mode === "add" ? DataSourceMode.ADD : DataSourceMode.UPDATE,
            data: data,
            width: "75%",
            onValidate: () => {
                const validationResults = layout.validatePayRate();
                if (validationResults && validationResults.length > 0) {
                    ValidationUtil.showFailedValidationDialog(validationResults, "Unable to save this record because data is missing or invalid.");
                }
                return validationResults;
            },
            onSave: (updatedData: ModelRow) => {
                updatedData.set("rate_details", layout.layoutPayRateDetail.sourceDrsPayrate.data || []);
                if (mode === "add") {
                    tableDriverPayRates.addRow(updatedData, null, { display: true, addToData: true });
                } else {
                    if (selectedRow) {
                        const rateDetailLayout = rateDetailsMap.get(selectedRow.data.get("primary_key"));
                        if (rateDetailLayout) {
                            rateDetailLayout.replaceMainDatasource(layout.layoutPayRateDetail.sourceDrsPayrate);
                        }
                        selectedRow.saveChangesFromEdit(updatedData);
                        selectedRow.displayComponentValues();
                    }
                }
            },
            onClose: () => {
                layout.slideOut().then(() => {
                    selectedRow?.setExpandedAsync(true);
                });
            }
        });
    }
}
